import ReactJsonView, { InteractionProps } from "@microlink/react-json-view";
import classNames from "classnames";
import { Form, Formik, FormikProps } from "formik";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { array, object } from "yup";

import style from "./schedule-workflow-dialog.scss";
import DeleteConfirmationDialog from "components/confirmation/DeleteConfirmationDialog";
import Info from "components/icons/Info";
import { TitleIcon } from "components/icons/TitleIcon";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import Modal from "components/modal/Modal";
import Tooltip from "components/tooltip/Tooltip";
import { Frequency, ScheduleRequest, ScheduleResponse } from "domain/workflows";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { WorkflowService } from "services/workflows/WorkflowService";
import buttons from "styles/buttons.scss";
import form from "styles/form.scss";
import { buildScheduleSummary, padTime, ScheduleFormValues as UtilScheduleFormValues } from "utils/scheduleUtils";
import { getLocalTimezone, getTimezoneOptions } from "utils/timezones";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

type DayOfWeek = "MON" | "TUE" | "WED" | "THU" | "FRI" | "SAT" | "SUN";

interface ScheduleFormValues extends UtilScheduleFormValues {
    enableSchedule: boolean;
}

interface Props {
    workflowUuid: string;
    workflowService: WorkflowService;
    visible: boolean;
    onClose: () => void;
    onUpdate: () => void;
    initialParameters?: Record<string, unknown>;
}

const LAUNCH_SCHEDULE_OPTIONS: Record<Frequency, string> = {
    HOURLY: "ScheduleWorkflow.launchScheduleOptions.hourly",
    DAILY: "ScheduleWorkflow.launchScheduleOptions.daily",
    WEEKLY: "ScheduleWorkflow.launchScheduleOptions.weekly",
    MONTHLY: "ScheduleWorkflow.launchScheduleOptions.monthly",
};

const DAYS_OF_WEEK: DayOfWeek[] = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

const EVERY_HOURS_OPTIONS = [...Array(23).keys()].map((i) => String(i + 1));
const LAUNCH_TIME_HOURS_OPTIONS = [...Array(24).keys()].map(padTime);
const LAUNCH_TIME_MINUTES_OPTIONS = [...Array(60).keys()].map(padTime);
const LAST_DAY_VALUE = -1;
const DAY_OF_MONTH_OPTIONS = [...Array(31).keys()].map((i) => i + 1);

interface Result {
    title: string;
    message: string;
    visible: boolean;
    success: boolean;
}

const toFormValues = (schedule: ScheduleResponse): ScheduleFormValues => {
    const [launchTimeHours, launchTimeMinutes] = schedule.recurrence.time?.split(":") ?? ["01", "00"];
    return {
        launchSchedule: schedule.frequency,
        timezone: schedule.timezone,
        enableSchedule: schedule.enabled,
        everyHours: schedule.recurrence.every ?? 1,
        launchTimeHours,
        launchTimeMinutes,
        daysOfWeek: (schedule.recurrence.days as DayOfWeek[]) ?? ["MON"],
        dayOfMonth: schedule.recurrence.day ?? 1,
    };
};

const ScheduleWorkflowDialog: React.FunctionComponent<Props> = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [loading, setLoading] = React.useState<boolean>(false);
    const [fetchLoading, setFetchLoading] = React.useState<boolean>(false);
    const [existingSchedule, setExistingSchedule] = React.useState<ScheduleResponse | undefined>(undefined);
    const [result, setResult] = React.useState<Result>({ title: "", message: "", visible: false, success: false });
    const [deleteConfirmVisible, setDeleteConfirmVisible] = React.useState<boolean>(false);
    const [deleteLoading, setDeleteLoading] = React.useState<boolean>(false);
    const [parameters, setParameters] = React.useState<Record<string, unknown>>(props.initialParameters ?? {});
    const [resettingParameters, setResettingParameters] = React.useState<boolean>(false);
    const resetTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    React.useEffect(() => {
        return () => {
            clearTimeout(resetTimeoutRef.current);
        };
    }, []);

    React.useEffect(() => {
        if (!props.visible) {
            setExistingSchedule(undefined);
            setParameters(props.initialParameters ?? {});
            return;
        }
        const abortController = new AbortController();
        setFetchLoading(true);
        props.workflowService
            .fetchSchedule(props.workflowUuid, abortController)
            .then((schedule) => {
                setExistingSchedule(schedule);
                setParameters(schedule.parameters ?? {});
            })
            .catch(() => {
                setExistingSchedule(undefined);
                setParameters(props.initialParameters ?? {});
            })
            .finally(() => {
                setFetchLoading(false);
            });
        return () => {
            abortController.abort();
        };
    }, [props.visible, props.workflowUuid]);

    const initialValues: ScheduleFormValues =
        existingSchedule !== undefined
            ? toFormValues(existingSchedule)
            : {
                  launchSchedule: "HOURLY",
                  everyHours: 1,
                  launchTimeHours: "01",
                  launchTimeMinutes: "00",
                  timezone: getLocalTimezone(),
                  enableSchedule: false,
                  daysOfWeek: ["MON"],
                  dayOfMonth: 1,
              };

    const validationSchema = object().shape({
        daysOfWeek: array().when("launchSchedule", {
            is: "WEEKLY",
            then: array().min(1, t("ScheduleWorkflow.validation.daysOfWeekRequired")),
        }),
    });

    const hideModal = () => {
        props.onClose();
    };

    const hideResult = () => {
        setResult((prev) => ({ ...prev, visible: false }));
        props.onUpdate();
    };

    const handleResetParameters = () => {
        setParameters(props.initialParameters ?? {});
        setResettingParameters(true);
        resetTimeoutRef.current = setTimeout(() => {
            setResettingParameters(false);
        }, 1_000);
    };

    const handleJsonChange = (event: InteractionProps) => {
        setParameters(event.updated_src as Record<string, unknown>);
        return true;
    };

    const handleDeleteSchedule = () => {
        setDeleteConfirmVisible(true);
    };

    const handleConfirmDelete = () => {
        setDeleteLoading(true);
        props.workflowService
            .deleteSchedule(props.workflowUuid)
            .then(() => {
                setDeleteConfirmVisible(false);
                props.onClose();
                setResult({
                    title: t("ScheduleWorkflow.deleteSuccessTitle"),
                    message: t("ScheduleWorkflow.deleteSuccessMessage"),
                    visible: true,
                    success: true,
                });
            })
            .catch(() => {
                setDeleteConfirmVisible(false);
                props.onClose();
                setResult({
                    title: t("ScheduleWorkflow.deleteErrorTitle"),
                    message: t("ScheduleWorkflow.deleteErrorMessage"),
                    visible: true,
                    success: false,
                });
            })
            .finally(() => {
                setDeleteLoading(false);
            });
    };

    const handleSubmit = async (values: ScheduleFormValues) => {
        setLoading(true);
        const isUpdate = existingSchedule !== undefined;
        const base = { timezone: values.timezone, enabled: values.enableSchedule, parameters };
        const launchTime = `${padTime(values.launchTimeHours)}:${padTime(values.launchTimeMinutes)}`;
        const request: ScheduleRequest =
            values.launchSchedule === "HOURLY"
                ? { ...base, frequency: "HOURLY", recurrence: { every: values.everyHours } }
                : values.launchSchedule === "WEEKLY"
                  ? {
                        ...base,
                        frequency: "WEEKLY",
                        recurrence: { time: launchTime, days: values.daysOfWeek },
                    }
                  : values.launchSchedule === "MONTHLY"
                    ? {
                          ...base,
                          frequency: "MONTHLY",
                          recurrence: { time: launchTime, day: values.dayOfMonth },
                      }
                    : {
                          ...base,
                          frequency: "DAILY",
                          recurrence: { time: launchTime },
                      };

        const serviceCall = isUpdate
            ? props.workflowService.updateSchedule(props.workflowUuid, request)
            : props.workflowService.createSchedule(props.workflowUuid, request);

        await serviceCall
            .then(() => {
                props.onClose();
                setResult({
                    title: t(isUpdate ? "ScheduleWorkflow.updateSuccessTitle" : "ScheduleWorkflow.successTitle"),
                    message: t(isUpdate ? "ScheduleWorkflow.updateSuccessMessage" : "ScheduleWorkflow.successMessage"),
                    visible: true,
                    success: true,
                });
            })
            .catch(() => {
                props.onClose();
                setResult({
                    title: t(isUpdate ? "ScheduleWorkflow.updateErrorTitle" : "ScheduleWorkflow.errorTitle"),
                    message: t(isUpdate ? "ScheduleWorkflow.updateErrorMessage" : "ScheduleWorkflow.submissionError"),
                    visible: true,
                    success: false,
                });
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return (
        <>
            <Modal isOpen={props.visible} hideModal={hideModal} modalTitle={t("ScheduleWorkflow.title")}>
                <Formik
                    initialValues={initialValues}
                    enableReinitialize={true}
                    validationSchema={validationSchema}
                    validateOnChange={false}
                    validateOnBlur={false}
                    onSubmit={handleSubmit}
                >
                    {({ values, handleChange, setFieldValue, errors }: FormikProps<ScheduleFormValues>) => (
                        <Form>
                            <div className={style.formContainer}>
                                {fetchLoading || loading ? (
                                    <LoadingIndicator />
                                ) : (
                                    <>
                                        <div className={style.formRow}>
                                            <label className={style.label}>
                                                {t("ScheduleWorkflow.launchSchedule")}
                                            </label>
                                            <select
                                                name="launchSchedule"
                                                value={values.launchSchedule}
                                                onChange={handleChange}
                                                className={classNames(form.select, style.fullWidthSelect)}
                                                data-testid={
                                                    testIds.workArea.workflows.scheduleWorkflowDialog
                                                        .launchScheduleSelect
                                                }
                                            >
                                                {(Object.entries(LAUNCH_SCHEDULE_OPTIONS) as [Frequency, string][]).map(
                                                    ([value, translationKey]) => (
                                                        <option key={value} value={value}>
                                                            {t(translationKey)}
                                                        </option>
                                                    )
                                                )}
                                            </select>
                                        </div>
                                        {values.launchSchedule === "HOURLY" && (
                                            <div className={style.inlineFormRow}>
                                                <label htmlFor="everyHours" className={style.label}>
                                                    {t("ScheduleWorkflow.every")}
                                                </label>
                                                <select
                                                    name="everyHours"
                                                    id="everyHours"
                                                    value={values.everyHours}
                                                    onChange={(e) =>
                                                        setFieldValue("everyHours", Number(e.target.value))
                                                    }
                                                    className={classNames(form.select, style.timeSelect)}
                                                    data-testid={
                                                        testIds.workArea.workflows.scheduleWorkflowDialog
                                                            .everyHoursSelect
                                                    }
                                                >
                                                    {EVERY_HOURS_OPTIONS.map((hour) => (
                                                        <option key={hour} value={hour}>
                                                            {hour}
                                                        </option>
                                                    ))}
                                                </select>
                                                <span>{t("ScheduleWorkflow.hours")}</span>
                                            </div>
                                        )}

                                        {values.launchSchedule === "WEEKLY" && (
                                            <div className={style.formRow}>
                                                <label className={style.label}>
                                                    {t("ScheduleWorkflow.daysOfWeek")}
                                                </label>
                                                <div className={style.daysOfWeekContainer}>
                                                    {DAYS_OF_WEEK.map((day) => (
                                                        <button
                                                            key={day}
                                                            type="button"
                                                            className={
                                                                values.daysOfWeek.includes(day)
                                                                    ? style.selectedDayButton
                                                                    : style.dayButton
                                                            }
                                                            onClick={() => {
                                                                const isSelected = values.daysOfWeek.includes(day);
                                                                const newDays = isSelected
                                                                    ? values.daysOfWeek.filter((d) => d !== day)
                                                                    : [...values.daysOfWeek, day];
                                                                setFieldValue("daysOfWeek", newDays, true);
                                                            }}
                                                            data-testid={
                                                                testIds.workArea.workflows.scheduleWorkflowDialog
                                                                    .dayButton
                                                            }
                                                            data-type={day}
                                                        >
                                                            {t(`ScheduleWorkflow.dayAbbreviations.${day}`)}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {values.launchSchedule === "MONTHLY" && (
                                            <div className={style.formRow}>
                                                <label className={style.label}>
                                                    {t("ScheduleWorkflow.dayOfMonth")}
                                                    <Tooltip
                                                        content={
                                                            <ul className={style.dayOfMonthTooltipList}>
                                                                <li>{t("ScheduleWorkflow.dayOfMonthTooltipDay29")}</li>
                                                                <li>{t("ScheduleWorkflow.dayOfMonthTooltipDay30")}</li>
                                                                <li>{t("ScheduleWorkflow.dayOfMonthTooltipDay31")}</li>
                                                            </ul>
                                                        }
                                                        placement="right"
                                                    >
                                                        <span className={style.dayOfMonthTooltipIcon}>
                                                            <Info
                                                                borderColor={theme.contentBackgroundColor}
                                                                color={theme.infoIconBackgroundColor}
                                                                size="14"
                                                            />
                                                        </span>
                                                    </Tooltip>
                                                </label>
                                                <select
                                                    name="dayOfMonth"
                                                    value={values.dayOfMonth}
                                                    onChange={(e) =>
                                                        setFieldValue("dayOfMonth", Number(e.target.value))
                                                    }
                                                    className={classNames(form.select, style.dayOfTheMonthSelect)}
                                                    data-testid={
                                                        testIds.workArea.workflows.scheduleWorkflowDialog
                                                            .dayOfMonthSelect
                                                    }
                                                >
                                                    {DAY_OF_MONTH_OPTIONS.map((day) => (
                                                        <option key={day} value={day}>
                                                            {day}
                                                        </option>
                                                    ))}
                                                    <option value={LAST_DAY_VALUE}>
                                                        {t("ScheduleWorkflow.lastDay")}
                                                    </option>
                                                </select>
                                            </div>
                                        )}

                                        {(values.launchSchedule === "DAILY" ||
                                            values.launchSchedule === "WEEKLY" ||
                                            values.launchSchedule === "MONTHLY") && (
                                            <div className={style.formRow}>
                                                <label className={style.label}>
                                                    {t("ScheduleWorkflow.launchTime")}
                                                </label>
                                                <div className={style.timeInputs}>
                                                    <select
                                                        name="launchTimeHours"
                                                        value={values.launchTimeHours}
                                                        onChange={handleChange}
                                                        className={classNames(form.select, style.timeSelect)}
                                                        data-testid={
                                                            testIds.workArea.workflows.scheduleWorkflowDialog
                                                                .launchTimeHoursSelect
                                                        }
                                                    >
                                                        {LAUNCH_TIME_HOURS_OPTIONS.map((hour) => (
                                                            <option key={hour} value={hour}>
                                                                {hour}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <span className={style.timeSeparator}>:</span>
                                                    <select
                                                        name="launchTimeMinutes"
                                                        value={values.launchTimeMinutes}
                                                        onChange={handleChange}
                                                        className={classNames(form.select, style.timeSelect)}
                                                        data-testid={
                                                            testIds.workArea.workflows.scheduleWorkflowDialog
                                                                .launchTimeMinutesSelect
                                                        }
                                                    >
                                                        {LAUNCH_TIME_MINUTES_OPTIONS.map((minute) => (
                                                            <option key={minute} value={minute}>
                                                                {minute}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        )}

                                        <div className={style.formRow}>
                                            <label className={style.label}>{t("ScheduleWorkflow.timezone")}</label>
                                            <select
                                                name="timezone"
                                                value={values.timezone}
                                                onChange={handleChange}
                                                className={classNames(form.select, style.fullWidthSelect)}
                                                data-testid={
                                                    testIds.workArea.workflows.scheduleWorkflowDialog.timezoneSelect
                                                }
                                            >
                                                {getTimezoneOptions().map(({ value, label }) => (
                                                    <option key={value} value={value}>
                                                        {label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className={style.inlineFormRow}>
                                            <label className={style.label}>{t("Common.active")}</label>
                                            <div
                                                role="switch"
                                                aria-checked={values.enableSchedule}
                                                tabIndex={0}
                                                onKeyDown={(e) => {
                                                    if (e.key === " " || e.key === "Enter") {
                                                        e.preventDefault();
                                                        setFieldValue("enableSchedule", !values.enableSchedule);
                                                    }
                                                }}
                                                className={classNames(style.customToggle, {
                                                    [style.toggleActive]: values.enableSchedule,
                                                })}
                                                onClick={() => setFieldValue("enableSchedule", !values.enableSchedule)}
                                                data-testid={
                                                    testIds.workArea.workflows.scheduleWorkflowDialog.enableToggle
                                                }
                                            >
                                                <div className={style.toggleSlider}>
                                                    <div className={style.toggleKnob} />
                                                </div>
                                            </div>
                                        </div>

                                        <div className={style.parametersSection}>
                                            <div className={style.parametersLabelRow}>
                                                <label className={style.label}>
                                                    {t("ScheduleWorkflow.parametersLabel")}
                                                </label>
                                                <div className={style.parametersActions}>
                                                    <button
                                                        type="button"
                                                        className={style.parametersResetLink}
                                                        onClick={handleResetParameters}
                                                        data-testid={
                                                            testIds.workArea.workflows.scheduleWorkflowDialog
                                                                .parametersResetButton
                                                        }
                                                    >
                                                        {t("ScheduleWorkflow.parametersResetButton")}
                                                    </button>
                                                    <Tooltip
                                                        content={
                                                            <ul className={style.dayOfMonthTooltipList}>
                                                                <li>{t("ScheduleWorkflow.launchMessage")}</li>
                                                                <li>{t("ScheduleWorkflow.warning")}</li>
                                                            </ul>
                                                        }
                                                        placement="right"
                                                    >
                                                        <span className={style.dayOfMonthTooltipIcon}>
                                                            <Info
                                                                borderColor={theme.contentBackgroundColor}
                                                                color={theme.infoIconBackgroundColor}
                                                                size="14"
                                                            />
                                                        </span>
                                                    </Tooltip>
                                                </div>
                                            </div>
                                            {resettingParameters ? (
                                                <div className={style.parametersEditorLoaderContainer}>
                                                    <LoadingIndicator />
                                                </div>
                                            ) : (
                                                <div
                                                    className={style.parametersEditorContainer}
                                                    data-testid={
                                                        testIds.workArea.workflows.scheduleWorkflowDialog
                                                            .parametersEditor
                                                    }
                                                >
                                                    <ReactJsonView
                                                        name={null}
                                                        src={parameters}
                                                        onAdd={handleJsonChange}
                                                        onEdit={handleJsonChange}
                                                        onDelete={handleJsonChange}
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        {errors.daysOfWeek ? (
                                            <p className={style.errorText}>{errors.daysOfWeek}</p>
                                        ) : (
                                            <p className={style.summaryText}>
                                                {buildScheduleSummary(values, t, LAST_DAY_VALUE)}
                                            </p>
                                        )}

                                        <div className={style.buttonContainer}>
                                            <button
                                                type="submit"
                                                className={buttons.primaryButtonWithoutIcon}
                                                data-testid={
                                                    testIds.workArea.workflows.scheduleWorkflowDialog.submitButton
                                                }
                                            >
                                                {existingSchedule !== undefined
                                                    ? t("ScheduleWorkflow.updateButton")
                                                    : t("ScheduleWorkflow.scheduleButton")}
                                            </button>
                                            <button
                                                type="button"
                                                className={buttons.secondaryButtonWithoutIcon}
                                                onClick={hideModal}
                                                data-testid={testIds.common.dialog.closeButton}
                                            >
                                                {t("Common.cancel")}
                                            </button>
                                            {existingSchedule !== undefined && (
                                                <button
                                                    type="button"
                                                    className={style.deleteLink}
                                                    onClick={() => {
                                                        usageStatisticsService.sendEvent({
                                                            category: Category.WORKFLOW,
                                                            action: Action.DELETE_SCHEDULE,
                                                        });
                                                        handleDeleteSchedule();
                                                    }}
                                                    data-testid={
                                                        testIds.workArea.workflows.scheduleWorkflowDialog
                                                            .deleteScheduleButton
                                                    }
                                                >
                                                    {t("Common.delete")}
                                                </button>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </Form>
                    )}
                </Formik>
            </Modal>
            <Modal
                isOpen={result.visible}
                hideModal={hideResult}
                modalTitle={result.title}
                titleIcon={<TitleIcon iconType={result.success ? "SUCCESS" : "ERROR"} />}
            >
                <div>
                    <div className={style.responseMessage}>{result.message}</div>
                    <div className={style.buttonContainer}>
                        <button className={buttons.primaryOkButton} onClick={hideResult}>
                            {t("Common.ok")}
                        </button>
                    </div>
                </div>
            </Modal>
            <DeleteConfirmationDialog
                modalDisplayed={deleteConfirmVisible}
                setModalDisplayed={setDeleteConfirmVisible}
                title={t("ScheduleWorkflow.deleteConfirmTitle")}
                introductionMessage={t("ScheduleWorkflow.deleteConfirmSubtitle")}
                confirmationMessage={t("Common.deleteConfirmationMessage")}
                handleCommand={handleConfirmDelete}
                loading={deleteLoading}
            />
        </>
    );
};

export default ScheduleWorkflowDialog;
